import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useMutationCreateCancellationReason,
  useMutationEditCancellationReason,
  useQueryGetCancellationReasonList,
} from "@gymflow/api";
import { CancellationReasonDTO } from "@gymflow/types";
import {
  Button,
  CheckIcon,
  CloseIcon,
  Input,
  Spinner,
} from "apps/portal/src/components/atoms";
import { ModalWrapper, useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import useGymflowModels from "apps/portal/src/store";
import { isAxiosError } from "axios";
import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { XIcon } from "../../atoms/icons/XIcon";
import { CancellationReasonItem } from "./components/CancellationReasonItem";

type CancellationReasonsModalProps = {
  onHide: () => void;
};

export const CancellationReasonsModal = ({
  onHide,
}: CancellationReasonsModalProps) => {
  const [reasonInput, setReasonInput] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();

  const { api } = useGymflowModels();
  const { clubId } = useClubSettings();
  const { t } = useTranslation();
  const { notifyDanger, toast } = useContext(ToastContext);

  const { data: cancellationReasons, isLoading: isLoadingCancellationReason } =
    useQueryGetCancellationReasonList({
      api,
      clubId,
    });

  const {
    mutateAsync: mutateAsyncCreateCancellationReason,
    isPending: isPendingCreateCancellationReason,
  } = useMutationCreateCancellationReason({ api });

  const {
    mutateAsync: mutateAsyncEditCancellationReason,
    isPending: isPendingEditCancellationReason,
  } = useMutationEditCancellationReason({ api });

  const onClickAdd = useCallback(async () => {
    try {
      await mutateAsyncCreateCancellationReason({ clubId, name: reasonInput });
      setReasonInput("");
      toast({
        message: t("components.cancellationReasonsModal.toast.reasonAdded"),
        type: "small",
        intent: "success",
      });
    } catch (e) {
      if (isAxiosError(e)) {
        notifyDanger(e);
      }
    }
  }, [
    clubId,
    mutateAsyncCreateCancellationReason,
    notifyDanger,
    reasonInput,
    t,
    toast,
  ]);

  const onStartEditReason = useCallback(
    async ({ name, id }: CancellationReasonDTO) => {
      setReasonInput(name);
      setEditingId(id);
      setIsEditing(true);
    },
    [],
  );

  const onFinishEditReason = useCallback(async () => {
    setIsEditing(false);
    setReasonInput("");
    setEditingId(undefined);
  }, []);

  const onEditReason = useCallback(async () => {
    if (!editingId) {
      return;
    }
    try {
      await mutateAsyncEditCancellationReason({
        clubId,
        reasonId: editingId,
        name: reasonInput,
      });
      onFinishEditReason();
    } catch (e) {
      if (isAxiosError(e)) {
        notifyDanger(e);
      }
    }
  }, [
    clubId,
    editingId,
    mutateAsyncEditCancellationReason,
    notifyDanger,
    onFinishEditReason,
    reasonInput,
  ]);

  return (
    <ModalWrapper
      className="flex flex-col gap-4 sm:!max-w-xl"
      onCancel={() => onHide()}
    >
      <div className="flex flex-row items-start justify-between">
        <div className="flex flex-col">
          <div className="text-lg font-semibold text-gray-950">
            {t("components.cancellationReasonsModal.title")}
          </div>
          <div className="text-sm font-medium text-gray-500">
            {t("components.cancellationReasonsModal.description")}
          </div>
        </div>
        <div className="cursor-pointer" onClick={() => onHide()}>
          <CloseIcon className="size-6" />
        </div>
      </div>
      <div className="flex flex-row items-center gap-4">
        <Input
          value={reasonInput}
          disabled={isPendingCreateCancellationReason}
          onChange={(e) => setReasonInput(e.currentTarget.value)}
          placeholder={t(
            "components.cancellationReasonsModal.input.placeholder",
          )}
        />
        <div className="flew-row flex w-[5.625rem] gap-1.5">
          {isEditing ? (
            <>
              <Button
                intent="secondary-outline"
                className="h-10 w-full"
                onClick={async () => await onEditReason()}
              >
                <div>
                  <CheckIcon className="size-4" />
                </div>
              </Button>
              <Button
                intent="default"
                className="h-10 w-full"
                disabled={isPendingEditCancellationReason}
                onClick={() => onFinishEditReason()}
              >
                <div>
                  <XIcon className="size-5" pathClassName="stroke-gray-950" />
                </div>
              </Button>
            </>
          ) : (
            <Button
              intent="secondary"
              className="text-gray-0 h-10 w-[5.625rem] gap-x-1.5"
              disabled={!reasonInput || isPendingCreateCancellationReason}
              onClick={async () => await onClickAdd()}
            >
              <FontAwesomeIcon icon={faPlus} />
              {t("common.add")}
            </Button>
          )}
        </div>
      </div>
      <div className="h-72 overflow-y-auto">
        {isLoadingCancellationReason ? (
          <Spinner />
        ) : (
          <div className="flex flex-col">
            {cancellationReasons?.map((item) => (
              <CancellationReasonItem
                key={item.id}
                reasonItem={item}
                isEditing={editingId === item.id}
                onStartEditReason={async (item) =>
                  await onStartEditReason(item)
                }
              />
            ))}
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};
